import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  useCreateMetadocMetadataMutation,
  useUpdateMetadocMetadataMutation,
} from "services/metadoc-api";

import type {
  AddCodePreset,
  CodeDateDetailValue,
  CodeMetadataValue,
  CodeSection,
  CodeStatus,
  EditableCodeValue,
  NonEmptyArray,
  EditableCodeDateDetail,
} from "../../utils/codeMetadataUtils";

type CodeEditorState = {
  codes: EditableCodeValue[];
  isCollapsed: boolean;
  isSaving: boolean;
  isAddingCode: boolean;
  lastError: string | null;
};

type CodeEditorAction =
  | { type: "CODES_LOADED"; payload: EditableCodeValue[] }
  | { type: "TOGGLE_COLLAPSE" }
  | {
      type: "UPDATE_CONDITION";
      metadataId: number;
      conditionType: CodeStatus;
    }
  | {
      type: "UPDATE_FIELD";
      metadataId: number;
      detailId: string;
      field: string;
      value: string;
    }
  | {
      type: "UPDATE_ASSOCIATION";
      metadataId: number;
      documentGroupId: number | null;
      provider: string;
      facility: string;
    }
  | { type: "ADD_CODE"; payload: EditableCodeValue }
  | { type: "UPDATE_CODE_PRESET"; metadataId: number; preset: AddCodePreset }
  | { type: "UPDATE_SECTION"; metadataId: number; section: CodeSection }
  | { type: "ADD_CODE_START" }
  | { type: "ADD_CODE_SUCCESS" }
  | { type: "ADD_CODE_ERROR"; error: string }
  | { type: "SAVE_START" }
  | { type: "SAVE_SUCCESS" }
  | { type: "SAVE_ERROR"; error: string };

const initialState: CodeEditorState = {
  codes: [],
  isCollapsed: false,
  isSaving: false,
  isAddingCode: false,
  lastError: null,
};

const codeEditorReducer = (
  state: CodeEditorState,
  action: CodeEditorAction,
): CodeEditorState => {
  switch (action.type) {
    case "CODES_LOADED":
      return {
        ...state,
        codes: action.payload,
      };

    case "TOGGLE_COLLAPSE":
      return {
        ...state,
        isCollapsed: !state.isCollapsed,
      };

    case "UPDATE_CONDITION":
      return {
        ...state,
        codes: state.codes.map((cv) =>
          cv.id === action.metadataId
            ? { ...cv, conditionType: action.conditionType }
            : cv,
        ),
      };

    case "UPDATE_SECTION":
      return {
        ...state,
        codes: state.codes.map((cv) =>
          cv.id === action.metadataId ? { ...cv, section: action.section } : cv,
        ),
      };

    case "UPDATE_FIELD":
      return {
        ...state,
        codes: state.codes.map((cv) =>
          cv.id === action.metadataId
            ? {
                ...cv,
                dateDetails: cv.dateDetails.map((detail) =>
                  detail.id === action.detailId
                    ? { ...detail, [action.field]: action.value }
                    : detail,
                ) as NonEmptyArray<EditableCodeDateDetail>,
              }
            : cv,
        ),
      };

    case "UPDATE_ASSOCIATION":
      return {
        ...state,
        codes: state.codes.map((cv) =>
          cv.id === action.metadataId
            ? {
                ...cv,
                documentGroupId: action.documentGroupId,
                provider: action.provider,
                facility: action.facility,
              }
            : cv,
        ),
      };

    case "ADD_CODE":
      return {
        ...state,
        codes: [action.payload, ...state.codes],
      };

    case "UPDATE_CODE_PRESET":
      return {
        ...state,
        codes: state.codes.map((cv) =>
          cv.id === action.metadataId
            ? {
                ...cv,
                code: action.preset.code,
                codeType: action.preset.codeType,
                dateDetails: cv.dateDetails.map((detail, detailIndex) =>
                  detailIndex === 0
                    ? { ...detail, description: action.preset.description }
                    : detail,
                ) as NonEmptyArray<EditableCodeDateDetail>,
              }
            : cv,
        ),
      };

    case "ADD_CODE_START":
      return {
        ...state,
        isAddingCode: true,
        lastError: null,
      };

    case "ADD_CODE_SUCCESS":
      return {
        ...state,
        isAddingCode: false,
      };

    case "ADD_CODE_ERROR":
      return {
        ...state,
        isAddingCode: false,
        lastError: action.error,
      };

    case "SAVE_START":
      return {
        ...state,
        isSaving: true,
        lastError: null,
      };

    case "SAVE_SUCCESS":
      return {
        ...state,
        isSaving: false,
      };

    case "SAVE_ERROR":
      return {
        ...state,
        isSaving: false,
        lastError: action.error,
      };

    default:
      return state;
  }
};

type UseCodeEditorArgs = {
  metadocId: number;
  initialCodes: EditableCodeValue[];
};

type UseCodeEditorReturn = {
  state: CodeEditorState;
  handleToggleCollapse: () => void;
  handleConditionTypeChange: (
    metadataId: number,
    conditionType: CodeStatus,
  ) => Promise<void>;
  handleDateDetailFieldChange: (
    metadataId: number,
    detailId: string,
    field: "date" | "description" | "notes" | "reference" | "pageNumber",
    value: string,
  ) => void;
  handleDateDetailFieldSave: (
    metadataId: number,
    detailId: string,
    field: "date" | "description" | "notes",
    value: string,
  ) => Promise<void>;
  handleAssociationChange: (
    metadataId: number,
    documentGroupId: number | null,
    provider: string,
    facility: string,
  ) => void;
  handleAssociationSave: (
    metadataId: number,
    documentGroupId: number | null,
    provider: string,
    facility: string,
  ) => Promise<void>;
  handleAddCode: (preset: AddCodePreset) => Promise<void>;
  handleCodePresetSave: (
    metadataId: number,
    preset: AddCodePreset,
  ) => Promise<void>;
  handleSectionChange: (
    metadataId: number,
    section: CodeSection,
  ) => Promise<void>;
  loadCodes: (codes: EditableCodeValue[]) => void;
};

const toValidPageNumber = (pageNumber: string): number | null => {
  if (!pageNumber.trim()) {
    return null;
  }

  const parsed = Number(pageNumber);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
};

const buildDateDetailsPayload = (
  dateDetails: EditableCodeDateDetail[],
): CodeDateDetailValue[] =>
  dateDetails.map((detail) => ({
    date: detail.date,
    description: detail.description,
    notes: detail.notes,
    page_number: toValidPageNumber(detail.pageNumber),
  }));

const toFacilityValue = (facility: string): string | undefined => {
  const normalized = facility.trim();
  return normalized ? normalized : undefined;
};

const toProviderValue = (provider: string): string | null => {
  const normalized = provider.trim();
  return normalized || null;
};

const isCodeMetadataValue = (
  value: CodeMetadataValue,
): value is CodeMetadataValue => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  return true;
};

/*
manages code editing state with reducer pattern
handles save mutations with error notifications
provides unified interface for code editing operations
*/
export const useCodeEditor = ({
  metadocId,
  initialCodes,
}: UseCodeEditorArgs): UseCodeEditorReturn => {
  const [state, dispatch] = useReducer(codeEditorReducer, {
    ...initialState,
    codes: initialCodes,
  });
  const codesRef = useRef(state.codes);

  const [updateMetadocMetadata] = useUpdateMetadocMetadataMutation();
  const [createMetadocMetadata] = useCreateMetadocMetadataMutation();

  useEffect(() => {
    codesRef.current = state.codes;
  }, [state.codes]);

  const saveMetadataUpdate = useCallback(
    async (
      metadataId: number,
      updatedValue: CodeMetadataValue,
      documentGroupId?: number | null,
      providerName?: string | null,
    ) => {
      dispatch({ type: "SAVE_START" });
      try {
        if (!isCodeMetadataValue(updatedValue)) {
          throw new Error("Invalid metadata payload");
        }

        await updateMetadocMetadata({
          metadataId,
          data: {
            value: updatedValue,
            ...(documentGroupId !== undefined
              ? { document_group: documentGroupId }
              : {}),
            ...(providerName !== undefined
              ? { provider_name: providerName }
              : {}),
          },
        }).unwrap();
        dispatch({ type: "SAVE_SUCCESS" });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        console.error("Failed to update metadata:", error);
        dispatch({ type: "SAVE_ERROR", error: errorMessage });
        KaboodleToaster.error("Failed to update code. Please try again.");
        throw error;
      }
    },
    [updateMetadocMetadata],
  );

  const loadCodes = useCallback((codes: EditableCodeValue[]) => {
    dispatch({ type: "CODES_LOADED", payload: codes });
  }, []);

  const handleToggleCollapse = useCallback(() => {
    dispatch({ type: "TOGGLE_COLLAPSE" });
  }, []);

  const handleConditionTypeChange = useCallback(
    async (metadataId: number, conditionType: CodeStatus) => {
      const codeValue = codesRef.current.find((cv) => cv.id === metadataId);
      if (!codeValue) {
        return;
      }

      dispatch({ type: "UPDATE_CONDITION", metadataId, conditionType });

      const updatedValue: CodeMetadataValue = {
        code: codeValue.code,
        code_type: codeValue.codeType || undefined,
        facility: toFacilityValue(codeValue.facility),
        condition_type: conditionType,
        date_details: buildDateDetailsPayload(codeValue.dateDetails),
      };

      await saveMetadataUpdate(metadataId, updatedValue);
    },
    [saveMetadataUpdate],
  );

  const handleDateDetailFieldChange = useCallback(
    (
      metadataId: number,
      detailId: string,
      field: "date" | "description" | "notes" | "reference" | "pageNumber",
      value: string,
    ) => {
      dispatch({ type: "UPDATE_FIELD", metadataId, detailId, field, value });
    },
    [],
  );

  const handleDateDetailFieldSave = useCallback(
    async (
      metadataId: number,
      detailId: string,
      field: "date" | "description" | "notes",
      value: string,
    ) => {
      const codeValue = codesRef.current.find((cv) => cv.id === metadataId);
      if (!codeValue) {
        return;
      }

      const updatedDetails = codeValue.dateDetails.map((detail) =>
        detail.id === detailId ? { ...detail, [field]: value } : detail,
      );

      const updatedValue: CodeMetadataValue = {
        code: codeValue.code,
        code_type: codeValue.codeType || undefined,
        facility: toFacilityValue(codeValue.facility),
        condition_type: codeValue.conditionType,
        date_details: buildDateDetailsPayload(updatedDetails),
      };

      await saveMetadataUpdate(metadataId, updatedValue);
    },
    [saveMetadataUpdate],
  );

  const handleAssociationChange = useCallback(
    (
      metadataId: number,
      documentGroupId: number | null,
      provider: string,
      facility: string,
    ) => {
      dispatch({
        type: "UPDATE_ASSOCIATION",
        metadataId,
        documentGroupId,
        provider,
        facility,
      });
    },
    [],
  );

  const handleAssociationSave = useCallback(
    async (
      metadataId: number,
      documentGroupId: number | null,
      provider: string,
      facility: string,
    ) => {
      const codeValue = codesRef.current.find((cv) => cv.id === metadataId);
      if (!codeValue) {
        return;
      }

      const updatedValue: CodeMetadataValue = {
        code: codeValue.code,
        code_type: codeValue.codeType || undefined,
        facility: toFacilityValue(facility),
        condition_type: codeValue.conditionType,
        date_details: buildDateDetailsPayload(codeValue.dateDetails),
      };

      await saveMetadataUpdate(
        metadataId,
        updatedValue,
        documentGroupId,
        toProviderValue(provider),
      );
    },
    [saveMetadataUpdate],
  );

  const handleAddCode = useCallback(
    async (preset: AddCodePreset) => {
      const code = preset.code?.trim();
      const codeType = preset.codeType;
      const description = preset.description?.trim();
      if (!code || !codeType || !description) {
        throw new Error("Invalid code preset data");
      }

      const initialDetailValue: CodeDateDetailValue = {
        date: "",
        description,
        notes: "",
        page_number: null,
      };
      const initialCodeValue: CodeMetadataValue = {
        code,
        code_type: codeType,
        facility: undefined,
        condition_type: "unrelated",
        date_details: [initialDetailValue],
      };

      dispatch({ type: "ADD_CODE_START" });

      try {
        if (!isCodeMetadataValue(initialCodeValue)) {
          throw new Error("Invalid metadata payload");
        }

        const createdMetadata = await createMetadocMetadata({
          data: {
            metadoc: metadocId,
            type: "code",
            value: initialCodeValue,
          },
        }).unwrap();

        dispatch({
          type: "ADD_CODE",
          payload: {
            id: createdMetadata.id,
            code,
            codeType,
            conditionType: "unrelated",
            documentGroupId: null,
            provider: "",
            facility: "",
            section: "other",
            dateDetails: [
              {
                id: `${createdMetadata.id}-0`,
                date: "",
                description,
                notes: "",
                reference: "",
                pageNumber: "",
              },
            ],
          },
        });
        dispatch({ type: "ADD_CODE_SUCCESS" });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        console.error("Failed to create metadata:", error);
        dispatch({ type: "ADD_CODE_ERROR", error: errorMessage });
        KaboodleToaster.error("Failed to add code. Please try again.");
        throw error;
      }
    },
    [createMetadocMetadata, metadocId],
  );

  const handleCodePresetSave = useCallback(
    async (metadataId: number, preset: AddCodePreset) => {
      const codeValue = codesRef.current.find((cv) => cv.id === metadataId);
      if (!codeValue) {
        return;
      }

      dispatch({ type: "UPDATE_CODE_PRESET", metadataId, preset });

      const updatedDetails = codeValue.dateDetails.map((detail, detailIndex) =>
        detailIndex === 0
          ? { ...detail, description: preset.description }
          : detail,
      );

      const updatedValue: CodeMetadataValue = {
        code: preset.code,
        code_type: preset.codeType,
        facility: toFacilityValue(codeValue.facility),
        condition_type: codeValue.conditionType,
        date_details: buildDateDetailsPayload(updatedDetails),
      };

      await saveMetadataUpdate(metadataId, updatedValue);
    },
    [saveMetadataUpdate],
  );

  const handleSectionChange = useCallback(
    async (metadataId: number, section: CodeSection) => {
      const codeValue = codesRef.current.find((cv) => cv.id === metadataId);
      if (!codeValue) {
        return;
      }

      dispatch({ type: "UPDATE_SECTION", metadataId, section });

      const updatedValue: CodeMetadataValue = {
        code: codeValue.code,
        code_type: codeValue.codeType || undefined,
        section,
        facility: toFacilityValue(codeValue.facility),
        condition_type: codeValue.conditionType,
        date_details: buildDateDetailsPayload(codeValue.dateDetails),
      };

      await saveMetadataUpdate(metadataId, updatedValue);
    },
    [saveMetadataUpdate],
  );

  return {
    state,
    handleToggleCollapse,
    handleConditionTypeChange,
    handleDateDetailFieldChange,
    handleDateDetailFieldSave,
    handleAssociationChange,
    handleAssociationSave,
    handleAddCode,
    handleCodePresetSave,
    handleSectionChange,
    loadCodes,
  };
};
